import {useCallback, useEffect, useRef, useState} from 'react';
import {useMutation} from '@apollo/client';
import {
  PRESIGN_POD_EXEMPT_MATERIAL,
  PresignPodExemptMaterialResponse,
} from '../graphql/podExempt/mutations';
import {PodExemptMaterialType} from '../graphql/podExempt/types';

export interface UploadedMaterial {
  objectKey: string;
  materialType: PodExemptMaterialType;
  fileName: string;
  /** 本地预览 URL（objectURL，组件卸载时由调用方 revoke） */
  previewUrl: string;
}

/** 与后端 presign 白名单一致（PodExemptReportApplicationImpl.ALLOWED_EXTENSIONS） */
/** 与后端 presign 白名单同口径（D14：其他文件支持 PDF/DOC/XLS/CSV/压缩包） */
export const POD_EXEMPT_ALLOWED_EXTENSIONS = [
  'jpg', 'jpeg', 'png', 'webp', 'heic', 'mp4', 'mov',
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'zip',
] as const;

/**
 * 扩展名 → MIME 兜底：部分系统对 heic/doc 等 file.type 为空串，
 * 而后端 presign 白名单是精确 MIME（禁裸前缀），空串或 octet-stream 会被拒。
 */
const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  zip: 'application/zip',
};

/** 直传超时：100MB 视频慢网留足余量，超时中断并报错而非永久转圈 */
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

function effectiveMime(file: File, ext: string): string {
  return file.type || MIME_BY_EXT[ext] || 'application/octet-stream';
}

function inferMaterialType(mime: string): PodExemptMaterialType {
  if (mime.startsWith('image/')) {
    return 'PHOTO';
  }
  if (mime.startsWith('video/')) {
    return 'VIDEO';
  }
  return 'FILE';
}

/** 上传失败阶段，与后端 PodExemptMetricsConstants.STAGE_* 白名单一一对应 */
export type PodExemptUploadStage = 'PRESIGN' | 'PUT' | 'TIMEOUT';

/**
 * 结构化上传失败：调用方据此上报失败详情。
 *
 * presign 之后的 PUT 是浏览器直连 R2、不经过 api-server，服务端对失败零感知，
 * 只能由前端把阶段 / 状态码 / 耗时回传（2026-09-21 线上 30~40% 上传失败即因此
 * 只能靠用户截图发现）。
 */
export class PodExemptUploadError extends Error {
  constructor(
    readonly stage: PodExemptUploadStage,
    readonly durationMs: number,
    readonly objectKey: string | null = null,
    readonly httpStatus: number | null = null,
    readonly cause?: unknown,
  ) {
    super(`pod exempt upload failed at ${stage}`);
    this.name = 'PodExemptUploadError';
  }

  /** 上报用的错误名：网络层失败时原始错误名（TypeError 等）更有定位价值 */
  get reportedErrorName(): string {
    const inner = this.cause as Error | undefined;
    return inner?.name ? `${this.name}/${inner.name}` : this.name;
  }
}

/**
 * 判定 PUT 阶段的失败性质。返回 `null` 表示「主动取消」，调用方应静默跳过、不报错。
 *
 * 独立成纯函数是因为这里出过 bug：超时与主动取消都表现为 `AbortError`，早先只按
 * `name === 'AbortError'` 判断就一律静默，结果慢网下用户等满 5 分钟后既没有图也没有
 * 任何提示，连反馈都无从反馈。区分两者的唯一依据是 `cancelled` 标记（由 cancel()
 * 与卸载置位），逻辑虽短但必须被测试守住。
 */
export function classifyPutFailure(
  error: unknown,
  cancelled: boolean,
): PodExemptUploadStage | null {
  const isAbort = (error as Error)?.name === 'AbortError';
  if (isAbort) {
    return cancelled ? null : 'TIMEOUT';
  }
  return 'PUT';
}

/**
 * 材料直传：presign 记账 → PUT 直传 R2 → 返回 objectKey（提交上报时核销）。
 * 失败可对同一文件重试（重试会生成新 objectKey，旧记账行由后端 48h 孤儿清理兜底）。
 */
export function usePodExemptMaterialUpload() {
  const [presign] = useMutation<PresignPodExemptMaterialResponse>(PRESIGN_POD_EXEMPT_MATERIAL);
  // 记录类型而非布尔：调用方的串行闸门要"任一类型在传就拦"，但展示层只能点亮
  // 正在传的那个区——三区共用布尔会让照片上传时视频/文件区也显示「上传中」
  const [uploadingType, setUploadingType] = useState<PodExemptMaterialType | null>(null);
  // 当前在飞的 PUT：关抽屉/卸载时 abort，避免 100MB 上行继续跑、材料在关闭后「复活」
  const abortRef = useRef<AbortController | null>(null);
  // 区分「主动取消」与「超时中断」：两者都表现为 AbortError，但前者不该报错、后者必须报。
  // 早先只靠 name==='AbortError' 判断，把超时一并吞掉了——慢网下用户等满 5 分钟后
  // 既没有图也没有提示，连反馈都无从反馈。
  const cancelledRef = useRef(false);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  // 组件卸载时兜底中断（同样属于主动取消）
  useEffect(
    () => () => {
      cancelledRef.current = true;
      abortRef.current?.abort();
    },
    [],
  );

  const upload = useCallback(
    async (file: File): Promise<UploadedMaterial> => {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!POD_EXEMPT_ALLOWED_EXTENSIONS.includes(ext as never)) {
        throw new Error(`unsupported extension: ${ext}`);
      }
      const mimeType = effectiveMime(file, ext);
      const materialType = inferMaterialType(mimeType);
      setUploadingType(materialType);
      // AbortController + setTimeout 兼具「超时兜底」与「主动取消」，并规避 AbortSignal.timeout 的浏览器兼容缺口
      const controller = new AbortController();
      abortRef.current = controller;
      cancelledRef.current = false;
      const timer = window.setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
      const startedAt = Date.now();
      const elapsed = () => Date.now() - startedAt;
      // objectKey 在 presign 返回后才有；提到 try 外，PUT 阶段失败时上报才能带上它
      let objectKey: string | null = null;
      try {
        let presigned;
        try {
          const {data} = await presign({
            variables: {
              materialType,
              fileName: file.name,
              mimeType,
              fileSize: file.size,
            },
          });
          presigned = data?.presignPodExemptMaterial;
        } catch (e) {
          throw new PodExemptUploadError('PRESIGN', elapsed(), null, null, e);
        }
        if (!presigned) {
          throw new PodExemptUploadError('PRESIGN', elapsed());
        }
        objectKey = presigned.objectKey;
        // file.slice() 返回 type 为空串的 Blob：既不让浏览器自动补 Content-Type 头
        // （R2 桶 CORS 的 AllowedHeaders 为空，预检带该头会 403），又是零拷贝——
        // 不能用 await file.arrayBuffer()，那会把整个文件（视频可达 100MB）读进 JS 堆
        let response: Response;
        try {
          response = await fetch(presigned.uploadUrl, {
            method: 'PUT',
            body: file.slice(),
            signal: controller.signal,
          });
        } catch (e) {
          const stage = classifyPutFailure(e, cancelledRef.current);
          // null = 主动取消：原样抛出 AbortError，由调用方静默跳过
          if (stage === null) {
            throw e;
          }
          throw new PodExemptUploadError(stage, elapsed(), objectKey, null, e);
        }
        if (!response.ok) {
          throw new PodExemptUploadError('PUT', elapsed(), objectKey, response.status);
        }
        return {
          objectKey: presigned.objectKey,
          materialType,
          fileName: file.name,
          previewUrl: URL.createObjectURL(file),
        };
      } finally {
        window.clearTimeout(timer);
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setUploadingType(null);
      }
    },
    [presign],
  );

  return {upload, cancel, uploading: uploadingType !== null, uploadingType};
}
